import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  Stack,
  Avatar,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tooltip,
  Snackbar,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Close as CloseIcon,
  EventAvailable as EventIcon,
  SportsScore as MatchIcon,
  Coffee as BreakIcon,
  EmojiEvents as CeremonyIcon,
  LocalActivity as TombolaIcon,
  MoreHoriz as OtherIcon,
  Schedule as ScheduleIcon,
  Place as VenueIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AutoAwesome as AutoScheduleIcon,
  DragIndicator as DragIcon,
  Undo as UndoIcon,
  SwapHoriz as SwapIcon,
  CompareArrows as CascadeIcon,
  DeleteOutline as DeleteIcon,
  AddCircleOutline as AddEventIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { Tournament, TournamentEvent, TournamentEventType, ScheduleBlock } from "../types";
import { apiRequest } from "../config/api";
import { autoScheduleMatchesBestOf, analyzePlayerWaitingTimes, detectDuplicatePlayers, type PlayerMatchSchedule } from "../utils/scheduleUtils";
import { getBracketNames } from "../utils/bracketNames";
import { useOrganization } from "../contexts/OrganizationContext";
import WaitingTimeReportDialog from "./WaitingTimeReportDialog";

interface TournamentSchedulePanelProps {
  tournament: Tournament | null;
  onUpdate?: () => void; // Callback to refresh tournament data
  readOnly?: boolean; // If true, editing is disabled
}

interface ScheduleItem {
  time: string;
  type: "match" | "event" | "block";
  title: string;
  subtitle?: string;
  eventType?: string;
  description?: string;
  courtNumber?: number;
  duration?: number;
  matchId?: string; // For identifying which match to update
  groupIndex?: number;
  matchIndex?: number;
  bracketType?: "mastersCup" | "tourCup";
  isPlaceholder?: boolean; // For reserved knockout slots
  isCancelled?: boolean;   // Match marked as cancelled
  players?: string; // Compact "P1 / P2 · P3 / P4" shown under the title when team has a custom name
}

// --- @dnd-kit wrapper components (hooks require their own component) ---

interface DroppableCellProps {
  id: string;
  disabled: boolean;
  children: (isOver: boolean) => React.ReactNode;
}

const DroppableCell: React.FC<DroppableCellProps> = ({ id, disabled, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id, disabled });
  return <div ref={setNodeRef} style={{ height: "100%" }}>{children(isOver)}</div>;
};

interface DraggableMatchCardProps {
  id: string;
  disabled: boolean;
  children: React.ReactNode;
}

const DraggableMatchCard: React.FC<DraggableMatchCardProps> = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        height: "100%",
        opacity: isDragging ? 0.4 : 1,
        cursor: disabled ? "default" : "grab",
        touchAction: "none",
      }}
    >
      {children}
    </div>
  );
};

interface MatchCardContentProps {
  match: ScheduleItem;
  isPendingChange: boolean | "" | undefined;
  isEditing: boolean;
  readOnly: boolean;
  numberOfCourts: number;
  editTime: string;
  setEditTime: (v: string) => void;
  editCourt: number;
  setEditCourt: (v: number) => void;
  saving: boolean;
  handleSaveMatch: (match: ScheduleItem) => void;
  setEditingMatch: (id: string | null) => void;
  getWaitingTimeColor: (matchId: string) => { bg: string; border: string; waitDesc: string } | null;
  onCancelKnockout?: (match: ScheduleItem) => void;
  onUncancelKnockout?: (match: ScheduleItem) => void;
  isCancelling?: boolean;
  onDeleteBlock?: (match: ScheduleItem) => void;
}

const MatchCardContent: React.FC<MatchCardContentProps> = ({
  match, isPendingChange, isEditing, readOnly, numberOfCourts,
  editTime, setEditTime, editCourt, setEditCourt, saving,
  handleSaveMatch, setEditingMatch, getWaitingTimeColor,
  onCancelKnockout, onUncancelKnockout, isCancelling, onDeleteBlock,
}) => {
  if (isEditing && !readOnly) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="caption" color="primary" gutterBottom>
          Editing
        </Typography>
        <Stack spacing={1}>
          <TextField
            label="Time"
            type="time"
            value={editTime}
            onChange={(e) => setEditTime(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Court</InputLabel>
            <Select
              value={editCourt}
              onChange={(e) => setEditCourt(Number(e.target.value))}
              label="Court"
            >
              {Array.from({ length: numberOfCourts }, (_, i) => i + 1).map(
                (court) => (
                  <MenuItem key={court} value={court}>
                    Court {court}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={0.5}>
            <Button
              variant="contained"
              onClick={() => handleSaveMatch(match)}
              disabled={saving}
              size="small"
              fullWidth
            >
              Save
            </Button>
            <Button
              variant="outlined"
              onClick={() => setEditingMatch(null)}
              size="small"
              fullWidth
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  const isBlock = match.type === "block";
  const waitingColor = !match.isPlaceholder && !isBlock ? getWaitingTimeColor(match.matchId || '') : null;
  const isDragDisabled = readOnly || !match.matchId;
  const isKnockout = !!match.bracketType;
  const isCancelled = match.isCancelled;

  return (
    <DraggableMatchCard id={match.matchId!} disabled={isDragDisabled}>
      <Paper
        elevation={2}
        sx={{
          p: 1,
          height: "100%",
          bgcolor: isCancelled
            ? "rgba(0,0,0,0.06)"
            : isPendingChange
            ? "warning.lighter"
            : isBlock
            ? "rgba(97, 97, 97, 0.12)"
            : (match.isPlaceholder
                ? "action.hover"
                : (waitingColor?.bg || "info.lighter")),
          borderLeft: "3px solid",
          borderLeftColor: isCancelled
            ? "text.disabled"
            : isPendingChange
            ? "warning.main"
            : isBlock
            ? "text.secondary"
            : (match.isPlaceholder
                ? "warning.main"
                : (waitingColor?.border || "info.main")),
          borderStyle: match.isPlaceholder || isBlock ? "dashed" : "solid",
          transition: "all 0.2s",
          "&:hover": {
            boxShadow: 3,
            transform: isDragDisabled ? "none" : "scale(1.02)",
          },
          cursor: isDragDisabled ? "default" : "grab",
          opacity: isCancelled ? 0.55 : 1,
          userSelect: "none",
        }}
        title={isCancelled ? "Cancelled" : isBlock ? "Blocked court — drag to move" : (waitingColor?.waitDesc || "Drag to move")}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {!readOnly && !isCancelled && (
            isBlock
              ? <BlockIcon fontSize="small" sx={{ color: "text.secondary", fontSize: "1rem" }} />
              : <DragIcon fontSize="small" sx={{ color: "text.secondary", fontSize: "1rem" }} />
          )}
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              flex: 1,
              fontSize: "0.85rem",
              fontStyle: match.isPlaceholder ? "italic" : "normal",
              color: isCancelled ? "text.disabled" : (match.isPlaceholder ? "text.secondary" : "inherit"),
              textDecoration: isCancelled ? "line-through" : "none",
            }}
          >
            {match.title}
          </Typography>
          {isBlock && !readOnly && onDeleteBlock && (
            <Tooltip title="Remove this block">
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onDeleteBlock(match); }}
                  sx={{ p: 0.25, color: "error.main" }}
                >
                  <DeleteIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {/* Cancel / Uncancel button — only on knockout matches, not in readOnly mode */}
          {isKnockout && !readOnly && isCancelled && onUncancelKnockout && (
            <Tooltip title="Restore this match">
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onUncancelKnockout(match); }}
                  disabled={isCancelling}
                  sx={{ p: 0.25, color: "primary.main" }}
                >
                  <UndoIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {isKnockout && !readOnly && !isCancelled && onCancelKnockout && (
            <Tooltip title="Cancel this match">
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onCancelKnockout(match); }}
                  disabled={isCancelling}
                  sx={{ p: 0.25, color: "error.main" }}
                >
                  <DeleteIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {isKnockout && match.matchIndex !== undefined
            ? `Match #${match.matchIndex + 1} · ${match.subtitle}`
            : match.subtitle}
        </Typography>
        {match.players && !isCancelled && (
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 0.25, fontSize: "0.7rem", color: "text.secondary", lineHeight: 1.3, opacity: 0.8 }}
          >
            {match.players}
          </Typography>
        )}
        {isCancelled && (
          <Chip label="Cancelled" size="small" color="default"
            sx={{ mt: 0.5, height: 18, fontSize: "0.65rem" }} />
        )}
        {!isCancelled && match.duration && (
          <Chip
            label={`${match.duration} min`}
            size="small"
            variant="outlined"
            sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
          />
        )}
        {!isCancelled && isPendingChange && (
          <Chip
            label="Moved"
            size="small"
            color="warning"
            sx={{ mt: 0.5, height: 20, fontSize: "0.65rem" }}
          />
        )}
      </Paper>
    </DraggableMatchCard>
  );
};

const TournamentSchedulePanel: React.FC<TournamentSchedulePanelProps> = ({
  tournament,
  onUpdate,
  readOnly = false,
}) => {
  const { currentOrg } = useOrganization();
  const bracketNames = getBracketNames(currentOrg?.slug);
  const [editingMatch, setEditingMatch] = useState<string | null>(null);
  const [editTime, setEditTime] = useState("");
  const [editCourt, setEditCourt] = useState(1);
  const [saving, setSaving] = useState(false);
  const [autoScheduling, setAutoScheduling] = useState(false);
  const [showWaitingReport, setShowWaitingReport] = useState(false);

  // Add Event form state
  const [showAddEventForm, setShowAddEventForm] = useState(false);
  const [newEventName, setNewEventName] = useState("");
  const [newEventType, setNewEventType] = useState<TournamentEventType>("break");
  const [newEventTime, setNewEventTime] = useState("");
  const [newEventDuration, setNewEventDuration] = useState(30);
  const [newEventDescription, setNewEventDescription] = useState("");
  const [addingEvent, setAddingEvent] = useState(false);

  // Add Block form state
  const [showAddBlockForm, setShowAddBlockForm] = useState(false);
  const [newBlockName, setNewBlockName] = useState("");
  const [newBlockTime, setNewBlockTime] = useState("");
  const [newBlockDuration, setNewBlockDuration] = useState(30);
  const [newBlockCourts, setNewBlockCourts] = useState<number[]>([]);
  const [addingBlock, setAddingBlock] = useState(false);

  // Drag & Drop state
  const [pendingChanges, setPendingChanges] = useState<Map<string, { time: string; court: number }>>(new Map());
  const [dragError, setDragError] = useState<string | null>(null);
  const [showDragError, setShowDragError] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [swapMode, setSwapMode] = useState(false);
  const [cancellingMatch, setCancellingMatch] = useState<string | null>(null);

  // @dnd-kit sensor with a small activation distance to distinguish click vs drag
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Calculate waiting time analysis for visual indicators (must be before early return)
  const waitingAnalysis = React.useMemo(() => {
    if (!tournament) return { playerSchedules: [], tournamentAverage: 0, fairnessScore: 0, warnings: [] };
    return analyzePlayerWaitingTimes(tournament);
  }, [tournament]);

  // Profit preview — always derived from the current tournament data whenever a schedule exists
  const profitPreview = React.useMemo(() => {
    if (!tournament) return null;

    const matchDuration = tournament.timedMatchDuration || 20;
    const pricePerSlot  = tournament.courtPricePerSlot;
    const feePerTeam    = tournament.registrationFeePerTeam;

    // Collect every scheduled match that has a start time and court number
    const scheduledMatches: { startMs: number; endMs: number; court: number }[] = [];

    tournament.groups?.forEach(g =>
      g.matchResults?.forEach(m => {
        if (m.startTime && m.courtNumber) {
          const startMs = new Date(m.startTime).getTime();
          scheduledMatches.push({ startMs, endMs: startMs + matchDuration * 60000, court: m.courtNumber });
        }
      })
    );
    [
      ...(tournament.knockoutBracket?.mastersCup || []),
      ...(tournament.knockoutBracket?.tourCup    || []),
    ].forEach(m => {
      // Include placeholders — they still occupy court time even before teams are known
      if (m.startTime && m.courtNumber) {
        const startMs = new Date(m.startTime).getTime();
        scheduledMatches.push({ startMs, endMs: startMs + matchDuration * 60000, court: m.courtNumber });
      }
    });

    if (scheduledMatches.length === 0) return null;

    const firstMatchMs  = Math.min(...scheduledMatches.map(m => m.startMs));
    const lastMatchEndMs = Math.max(...scheduledMatches.map(m => m.endMs));

    // Divide the full day into 90-min booking windows starting from the first match.
    // For each window: find the actual minutes used, round up to the nearest 30-min increment,
    // and charge proportionally (e.g. 60 min used → bill 60 min → cost = (60/90) × pricePerSlot).
    const windowMs       = 90 * 60 * 1000;
    const incrementMs    = 30 * 60 * 1000; // smallest bookable unit

    const bookingWindows: {
      start: number;
      courts: Set<number>;
      billedMinutes: number; // rounded up to nearest 30 min
      cost: number;
    }[] = [];

    let winStart = firstMatchMs;
    while (winStart < lastMatchEndMs) {
      const winEnd = winStart + windowMs;
      const courts = new Set<number>();
      let latestEndInWindowMs = winStart;

      scheduledMatches.forEach(m => {
        if (m.startMs < winEnd && m.endMs > winStart) {
          courts.add(m.court);
          if (m.endMs > latestEndInWindowMs) latestEndInWindowMs = m.endMs;
        }
      });

      if (courts.size > 0) {
        // Cap at the window size — if a match slightly overruns the boundary, bill the full 90 min
        const actualMs      = Math.min(latestEndInWindowMs - winStart, windowMs);
        const billedMs      = Math.ceil(actualMs / incrementMs) * incrementMs;
        const billedMinutes = Math.min(billedMs / 60000, 90);
        const cost          = pricePerSlot ? courts.size * (billedMinutes / 90) * pricePerSlot : 0;
        bookingWindows.push({ start: winStart, courts, billedMinutes, cost });
      }

      winStart = winEnd;
    }

    const totalCourtCost = bookingWindows.reduce((sum, w) => sum + w.cost, 0);

    const totalRevenue = feePerTeam
      ? (tournament.registeredTeams?.length || 0) * feePerTeam
      : 0;

    const profit = totalRevenue - totalCourtCost;

    const startLabel = new Date(firstMatchMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const endLabel   = new Date(lastMatchEndMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    return {
      totalCourtCost, totalRevenue, profit,
      bookingWindows,
      startLabel, endLabel,
      pricePerSlot, feePerTeam,
    };
  }, [tournament]);

  if (!tournament) return null;
  
  // Helper to get waiting time color for a match
  const getWaitingTimeColor = (matchId: string): { bg: string; border: string; waitDesc: string } | null => {
    // Find if any player in this match has a short wait before/after
    const matchData = tournament.groups?.flatMap(g => 
      g.matchResults?.map(m => ({
        matchId: `group-${m.team1Id}-${m.team2Id}`,
        team1Id: m.team1Id,
        team2Id: m.team2Id,
        startTime: m.startTime,
      })) || []
    ).find(m => matchId.includes(m.team1Id) && matchId.includes(m.team2Id));
    
    if (!matchData || !matchData.startTime) return null;
    
    const matchTime = new Date(matchData.startTime);
    const team1 = tournament.registeredTeams?.find(t => t.id === matchData.team1Id);
    const team2 = tournament.registeredTeams?.find(t => t.id === matchData.team2Id);
    
    const playerIds = [
      team1?.player1Id,
      team1?.player2Id,
      team2?.player1Id,
      team2?.player2Id
    ].filter(Boolean) as string[];
    
    // Check waiting times for all players
    let minWaitBefore = Infinity;
    let minWaitAfter = Infinity;
    
    playerIds.forEach(playerId => {
      const playerSchedule = waitingAnalysis.playerSchedules.find(p => p.playerId === playerId);
      if (!playerSchedule) return;
      
      const matchIndex = playerSchedule.matches.findIndex(m => 
        new Date(m.startTime).getTime() === matchTime.getTime()
      );
      
      if (matchIndex > 0) {
        minWaitBefore = Math.min(minWaitBefore, playerSchedule.waitingTimes[matchIndex - 1] || Infinity);
      }
      if (matchIndex < playerSchedule.waitingTimes.length) {
        minWaitAfter = Math.min(minWaitAfter, playerSchedule.waitingTimes[matchIndex] || Infinity);
      }
    });
    
    const minWait = Math.min(minWaitBefore, minWaitAfter);
    
    if (minWait === Infinity) return null;
    
    // Color coding based on minimum waiting time
    if (minWait < 20) {
      return { 
        bg: 'rgba(244, 67, 54, 0.1)', 
        border: '#f44336',
        waitDesc: `⚠️ Rushed: ${minWait}min break`
      };
    } else if (minWait < 35) {
      return { 
        bg: 'rgba(255, 152, 0, 0.1)', 
        border: '#ff9800',
        waitDesc: `⏱️ Tight: ${minWait}min break`
      };
    } else {
      return { 
        bg: 'rgba(76, 175, 80, 0.1)', 
        border: '#4caf50',
        waitDesc: `✓ Good: ${minWait}min break`
      };
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case "break":
        return <BreakIcon />;
      case "ceremony":
        return <CeremonyIcon />;
      case "tombola":
        return <TombolaIcon />;
      default:
        return <OtherIcon />;
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case "break":
        return "#ff9800";
      case "ceremony":
        return "#9c27b0";
      case "tombola":
        return "#e91e63";
      default:
        return "#607d8b";
    }
  };

  // ============ DRAG & DROP HELPER FUNCTIONS ============
  
  /**
   * Extract player IDs from a match (for conflict detection)
   */
  const getMatchPlayerIds = (item: ScheduleItem): string[] => {
    if (!tournament || !item.matchId) return [];
    
    // For group matches
    if (item.groupIndex !== undefined && item.matchIndex !== undefined) {
      const group = tournament.groups?.[item.groupIndex];
      const match = group?.matchResults?.[item.matchIndex];
      if (!match) return [];
      
      const team1 = tournament.registeredTeams?.find(t => t.id === match.team1Id);
      const team2 = tournament.registeredTeams?.find(t => t.id === match.team2Id);
      
      return [team1?.player1Id, team1?.player2Id, team2?.player1Id, team2?.player2Id].filter(Boolean) as string[];
    }
    
    // For knockout matches
    if (item.bracketType && item.matchIndex !== undefined) {
      const bracket = tournament.knockoutBracket?.[item.bracketType];
      const match = bracket?.[item.matchIndex];
      if (!match || !match.team1Id || !match.team2Id) return [];
      
      const team1 = tournament.registeredTeams?.find(t => t.id === match.team1Id);
      const team2 = tournament.registeredTeams?.find(t => t.id === match.team2Id);
      
      return [team1?.player1Id, team1?.player2Id, team2?.player1Id, team2?.player2Id].filter(Boolean) as string[];
    }
    
    return [];
  };

  /**
   * Check if adding a match at a specific time would cause player conflicts
   */
  const checkPlayerConflicts = (time: string, newMatch: ScheduleItem, allMatches: ScheduleItem[]): boolean => {
    const newMatchPlayers = getMatchPlayerIds(newMatch);
    if (newMatchPlayers.length === 0) return false; // No players (placeholder match)
    
    // Check all other matches at the same time
    const matchesAtSameTime = allMatches.filter(m => 
      m.time === time && m.matchId !== newMatch.matchId
    );
    
    for (const existingMatch of matchesAtSameTime) {
      const existingPlayers = getMatchPlayerIds(existingMatch);
      const hasConflict = newMatchPlayers.some(playerId => existingPlayers.includes(playerId));
      if (hasConflict) return true;
    }
    
    return false;
  };

  /**
   * Handle discarding pending changes
   */
  const handleDiscardChanges = () => {
    setPendingChanges(new Map());
    setDragError(null);
  };

  /**
   * Mark a knockout match as cancelled and save immediately
   */
  const handleCancelKnockoutMatch = async (match: ScheduleItem) => {
    if (!tournament || !match.bracketType || match.matchIndex === undefined) return;
    if (!window.confirm(`Cancel "${match.title}"? This will mark the match as cancelled in the bracket and live display.`)) return;

    setCancellingMatch(match.matchId || null);
    try {
      const updatedTournament = JSON.parse(JSON.stringify(tournament));
      const bracket = updatedTournament.knockoutBracket?.[match.bracketType];
      if (bracket?.[match.matchIndex]) {
        bracket[match.matchIndex].isCancelled = true;
      }

      const response = await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({ knockoutBracket: updatedTournament.knockoutBracket }),
      });
      if (!response.ok) throw new Error("Failed to save");

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error cancelling match:", error);
      alert("Failed to cancel match. Please try again.");
    } finally {
      setCancellingMatch(null);
    }
  };

  /**
   * Restore a previously cancelled knockout match
   */
  const handleUncancelKnockoutMatch = async (match: ScheduleItem) => {
    if (!tournament || !match.bracketType || match.matchIndex === undefined) return;

    setCancellingMatch(match.matchId || null);
    try {
      const updatedTournament = JSON.parse(JSON.stringify(tournament));
      const bracket = updatedTournament.knockoutBracket?.[match.bracketType];
      if (bracket?.[match.matchIndex]) {
        bracket[match.matchIndex].isCancelled = false;
      }

      const response = await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({ knockoutBracket: updatedTournament.knockoutBracket }),
      });
      if (!response.ok) throw new Error("Failed to save");

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error restoring match:", error);
      alert("Failed to restore match. Please try again.");
    } finally {
      setCancellingMatch(null);
    }
  };

  /**
   * Save all pending changes to Firestore
   */
  const handleSavePendingChanges = async () => {
    if (!tournament || pendingChanges.size === 0) return;
    
    setSaving(true);
    try {
      const updatedTournament = JSON.parse(JSON.stringify(tournament));
      const baseDate = tournament.startDate ? new Date(tournament.startDate) : new Date();
      
      // Apply all pending changes
      pendingChanges.forEach((change, matchId) => {
        const [hours, minutes] = change.time.split(":").map(Number);
        const matchDate = new Date(baseDate);
        matchDate.setHours(hours, minutes, 0, 0);
        const isoTime = matchDate.toISOString();
        
        // Group matches: matchId format is "group-{groupIndex}-{matchIndex}"
        const groupMatch = matchId.match(/^group-(\d+)-(\d+)$/);
        if (groupMatch) {
          const groupIndex = parseInt(groupMatch[1]);
          const matchIndex = parseInt(groupMatch[2]);
          const group = updatedTournament.groups?.[groupIndex];
          const match = group?.matchResults?.[matchIndex];
          if (match) {
            match.startTime = isoTime;
            match.courtNumber = change.court;
          }
          return;
        }
        
        // Knockout matches: matchId format is "knockout-{bracketType}-{index}"
        const knockoutMatch = matchId.match(/^knockout-(mastersCup|tourCup)-(\d+)$/);
        if (knockoutMatch) {
          const bracketType = knockoutMatch[1];
          const index = parseInt(knockoutMatch[2]);
          const match = updatedTournament.knockoutBracket?.[bracketType]?.[index];
          if (match) {
            match.startTime = isoTime;
            match.courtNumber = change.court;
          }
          return;
        }

        // Blocks: matchId format is "block-{id}"
        const blockMatch = matchId.match(/^block-(.+)$/);
        if (blockMatch) {
          const blockId = blockMatch[1];
          const block = (updatedTournament.scheduleBlocks as ScheduleBlock[] | undefined)?.find(b => b.id === blockId);
          if (block) {
            block.startTime = isoTime;
            block.courtNumber = change.court;
          }
        }
      });

      // Update on server
      const response = await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({
          groups: updatedTournament.groups,
          knockoutBracket: updatedTournament.knockoutBracket,
          scheduleBlocks: updatedTournament.scheduleBlocks,
        }),
      });
      
      if (!response.ok) {
        throw new Error("Failed to save changes");
      }
      
      // Clear pending changes
      setPendingChanges(new Map());
      setDragError(null);
      
      // Refresh tournament data
      if (onUpdate) {
        onUpdate();
      }
      
      alert("✓ Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
      alert("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  /**
   * Handle drag end - core drag and drop logic with cascade
   */
  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    
    // Dropped outside droppable area
    if (!over) return;
    
    const draggableId = String(active.id);
    const destDroppableId = String(over.id);
    
    // The draggableId is the matchId, source droppable is stored as data
    // Parse the droppable IDs: format is "slot-TIME-COURT"
    const destParts = destDroppableId.split('-');
    const destTime = destParts[1];
    const destCourtStr = destParts[2];
    if (!destTime || !destCourtStr) return;
    const destCourt = parseInt(destCourtStr);
    
    // Find the source match to get its current position
    const currentSchedule = buildSchedule();
    const draggedMatch = currentSchedule.find(item => item.matchId === draggableId);
    if (!draggedMatch) return;
    
    // Use pending position if match was already moved, otherwise use original
    const pendingPos = pendingChanges.get(draggableId);
    const sourceTime = pendingPos ? pendingPos.time : formatTime(draggedMatch.time);
    const sourceCourt = pendingPos ? pendingPos.court : (draggedMatch.courtNumber || 1);
    
    // Dropped in same position
    if (sourceTime === destTime && sourceCourt === destCourt) return;
    
    console.log('Drag end:', { 
      from: `${sourceTime} Court ${sourceCourt}`, 
      to: `${destTime} Court ${destCourt}`,
      matchId: draggableId 
    });
    
    // Rebuild schedule to get current state (with pending changes applied)
    const { grid: originalGrid, sortedTimeSlots } = buildScheduleGrid();
    const numberOfCourts = tournament.numberOfCourts || 4;
    
    // Build a grid that reflects pending changes (matches the visual state)
    const currentGrid = new Map(originalGrid);
    if (pendingChanges.size > 0) {
      currentGrid.forEach((courtMap) => courtMap.clear());
      const allMatches = currentSchedule.filter(item => item.type === "match" || item.type === "block");
      allMatches.forEach(m => {
        if (!m.matchId) return;
        const pending = pendingChanges.get(m.matchId);
        if (pending) {
          const courtMap = currentGrid.get(pending.time);
          if (courtMap) courtMap.set(pending.court, { ...m, time: `2000-01-01T${pending.time}` });
        } else {
          const time = formatTime(m.time);
          const courtMap = currentGrid.get(time);
          if (courtMap && m.courtNumber) courtMap.set(m.courtNumber, m);
        }
      });
    }
    
    // Check if destination has an event (can't drop on events)
    const destHasEvent = events.some(e => formatTime(e.time) === destTime);
    if (destHasEvent) {
      setDragError("Cannot drop matches on event time slots");
      setShowDragError(true);
      setTimeout(() => setShowDragError(false), 3000);
      return;
    }
    
    // Build linear slot array for cascade logic
    interface SlotPosition {
      time: string;
      court: number;
      match: ScheduleItem | null;
      isEvent: boolean;
    }
    
    const slots: SlotPosition[] = [];
    sortedTimeSlots.forEach(time => {
      const hasEvent = eventMap.has(time);
      for (let court = 1; court <= numberOfCourts; court++) {
        slots.push({
          time,
          court,
          match: hasEvent ? null : (currentGrid.get(time)?.get(court) || null),
          isEvent: hasEvent,
        });
      }
    });
    
    // Find source and destination indices
    const sourceIndex = slots.findIndex(s => s.time === sourceTime && s.court === sourceCourt);
    const destIndex = slots.findIndex(s => s.time === destTime && s.court === destCourt);
    
    if (sourceIndex === -1 || destIndex === -1) {
      console.error("Could not find source or destination slot");
      return;
    }
    
    // Create new slot arrangement
    const newSlots = [...slots];
    
    // Check if destination is empty
    const destMatch = slots[destIndex].match;
    
    if (!destMatch) {
      // Simple move to empty slot - works in both modes
      newSlots[sourceIndex] = { ...newSlots[sourceIndex], match: null };
      newSlots[destIndex] = { ...newSlots[destIndex], match: draggedMatch };
      console.log("Simple move to empty slot");
    } else if (swapMode) {
      // SWAP MODE: exchange the two cards, nothing else moves
      newSlots[sourceIndex] = { ...newSlots[sourceIndex], match: destMatch };
      newSlots[destIndex] = { ...newSlots[destIndex], match: draggedMatch };
      console.log("Swap mode: exchanged two cards");
    } else {
      // CASCADE: Slide matches to fill the gap left by the dragged match
      const movingForward = sourceIndex < destIndex; // Moving to a later time/court
      
      console.log(`Cascade mode: ${movingForward ? 'moving forward (slide backward to fill gap)' : 'moving backward (slide forward to fill gap)'}`);
      
      if (movingForward) {
        // Moving forward: Slide all matches from sourceIndex+1 to destIndex backward by one slot
        // This fills the gap at sourceIndex
        
        let slidingMatch: ScheduleItem | null;
        slidingMatch = draggedMatch; // Start with the dragged match going to destination
        
        // Work backwards from destination to source+1
        for (let i = destIndex; i > sourceIndex; i--) {
          if (newSlots[i].isEvent) {
            // Skip event slots - don't slide into them
            continue;
          }
          
          const currentMatch = newSlots[i].match;
          newSlots[i] = { ...newSlots[i], match: slidingMatch };
          slidingMatch = currentMatch;
        }
        
        // The last match slides into the now-empty source position
        newSlots[sourceIndex] = { ...newSlots[sourceIndex], match: slidingMatch };
        
      } else {
        // Moving backward: Slide all matches from destIndex to sourceIndex-1 forward by one slot
        // This fills the gap at sourceIndex
        
        let slidingMatch: ScheduleItem | null;
        slidingMatch = draggedMatch; // Start with the dragged match going to destination
        
        // Work forward from destination to source-1
        for (let i = destIndex; i < sourceIndex; i++) {
          if (newSlots[i].isEvent) {
            // Skip event slots - don't slide into them
            continue;
          }
          
          const currentMatch = newSlots[i].match;
          newSlots[i] = { ...newSlots[i], match: slidingMatch };
          slidingMatch = currentMatch;
        }
        
        // The last match slides into the now-empty source position
        newSlots[sourceIndex] = { ...newSlots[sourceIndex], match: slidingMatch };
      }
    }
    
    // Check for player conflicts at each time slot
    const timeSlotMatches = new Map<string, ScheduleItem[]>();
    newSlots.forEach(slot => {
      if (slot.match) {
        if (!timeSlotMatches.has(slot.time)) {
          timeSlotMatches.set(slot.time, []);
        }
        timeSlotMatches.get(slot.time)!.push(slot.match);
      }
    });
    
    const conflicts = Array.from(timeSlotMatches.entries());
    for (let i = 0; i < conflicts.length; i++) {
      const [time, matches] = conflicts[i];
      for (let j = 0; j < matches.length; j++) {
        for (let k = j + 1; k < matches.length; k++) {
          const players1 = getMatchPlayerIds(matches[j]);
          const players2 = getMatchPlayerIds(matches[k]);
          const conflict = players1.some(p => players2.includes(p));
          
          if (conflict) {
            // Identify the player actually shared between both matches — not just any
            // player from matches[j], which could be their opponent instead.
            const overlappingPlayerIds = players1.filter(p => players2.includes(p));
            const conflictingTeam = tournament.registeredTeams?.find(t =>
              overlappingPlayerIds.includes(t.player1Id || '') || overlappingPlayerIds.includes(t.player2Id || '')
            );
            setDragError(
              `Player conflict detected at ${time}! ` +
              `${conflictingTeam?.player1Name || conflictingTeam?.player2Name || 'A player'} would play in multiple matches simultaneously.`
            );
            setShowDragError(true);
            setTimeout(() => setShowDragError(false), 5000);
            return;
          }
        }
      }
    }
    
    // No conflicts! Update pending changes
    const newPendingChanges = new Map(pendingChanges);
    
    newSlots.forEach(slot => {
      if (slot.match && !slot.isEvent && slot.match.matchId) {
        const originalMatch = currentSchedule.find(m => m.matchId === slot.match!.matchId);
        const originalTime = originalMatch ? formatTime(originalMatch.time) : null;
        const originalCourt = originalMatch?.courtNumber || 0;
        
        if (originalTime !== slot.time || originalCourt !== slot.court) {
          // Position changed from original - add/update pending change
          newPendingChanges.set(slot.match.matchId, {
            time: slot.time,
            court: slot.court,
          });
        } else {
          // Back to original position - remove from pending changes
          newPendingChanges.delete(slot.match.matchId);
        }
      }
    });
    
    setPendingChanges(newPendingChanges);
    console.log(`✓ Move successful! ${newPendingChanges.size} pending changes`);
  };


  const handleAddEvent = async () => {
    if (!tournament || !newEventName.trim() || !newEventTime || !newEventDuration) return;

    setAddingEvent(true);
    try {
      const newEvent: TournamentEvent = {
        id: `event_${Date.now()}`,
        name: newEventName.trim(),
        type: newEventType,
        startTime: newEventTime,
        duration: newEventDuration,
        description: newEventDescription.trim() || undefined,
      };

      const updatedEvents = [...(tournament.events || []), newEvent];
      const tournamentWithEvent = { ...tournament, events: updatedEvents };

      // Auto-schedule around the new event if matches exist
      const hasMatches =
        tournament.groups?.some(g => g.matchResults && g.matchResults.length > 0) ||
        (tournament.knockoutBracket?.mastersCup?.length || 0) > 0 ||
        (tournament.knockoutBracket?.tourCup?.length || 0) > 0;

      let body: Record<string, unknown> = { events: updatedEvents };

      if (hasMatches && tournament.startTime && tournament.numberOfCourts) {
        const { result: scheduledTournament } = autoScheduleMatchesBestOf(tournamentWithEvent, 8);
        body = {
          events: updatedEvents,
          groups: scheduledTournament.groups,
          knockoutBracket: scheduledTournament.knockoutBracket,
        };
      }

      const response = await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error("Failed to save event");

      setShowAddEventForm(false);
      setNewEventName("");
      setNewEventTime("");
      setNewEventDuration(30);
      setNewEventType("break");
      setNewEventDescription("");

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error adding event:", error);
      alert("Failed to add event. Please try again.");
    } finally {
      setAddingEvent(false);
    }
  };

  /**
   * Add one or more court blocks (e.g. an outside booking or maintenance window).
   * Creates one ScheduleBlock per selected court, all sharing the same name/time/duration,
   * then places it on the schedule like a match — the organizer repositions it by dragging.
   */
  const handleAddBlock = async () => {
    if (!tournament || !newBlockName.trim() || !newBlockTime || !newBlockDuration || newBlockCourts.length === 0) return;

    setAddingBlock(true);
    try {
      const baseDate = tournament.startDate ? new Date(tournament.startDate) : new Date();
      const [hours, minutes] = newBlockTime.split(":").map(Number);
      const blockDate = new Date(baseDate);
      blockDate.setHours(hours, minutes, 0, 0);
      const isoTime = blockDate.toISOString();

      const newBlocks: ScheduleBlock[] = newBlockCourts.map((courtNumber, i) => ({
        id: `block_${Date.now()}_${i}`,
        name: newBlockName.trim(),
        startTime: isoTime,
        courtNumber,
        duration: newBlockDuration,
      }));

      const updatedBlocks = [...(tournament.scheduleBlocks || []), ...newBlocks];

      const response = await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({ scheduleBlocks: updatedBlocks }),
      });

      if (!response.ok) throw new Error("Failed to save block");

      setShowAddBlockForm(false);
      setNewBlockName("");
      setNewBlockTime("");
      setNewBlockDuration(30);
      setNewBlockCourts([]);

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error adding block:", error);
      alert("Failed to add block. Please try again.");
    } finally {
      setAddingBlock(false);
    }
  };

  /**
   * Remove a court block immediately (blocks aren't real matches, so no confirmation
   * dialog beyond the standard confirm — nothing else references them).
   */
  const handleDeleteBlock = async (match: ScheduleItem) => {
    if (!tournament || !match.matchId) return;
    const blockId = match.matchId.replace(/^block-/, "");
    if (!window.confirm(`Remove the "${match.title}" block?`)) return;

    try {
      const updatedBlocks = (tournament.scheduleBlocks || []).filter(b => b.id !== blockId);
      const response = await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({ scheduleBlocks: updatedBlocks }),
      });
      if (!response.ok) throw new Error("Failed to remove block");

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error removing block:", error);
      alert("Failed to remove block. Please try again.");
    }
  };

  const handleAutoSchedule = async () => {
    if (!tournament) return;

    // Validate required fields
    if (!tournament.startTime) {
      alert("Please set a Start Time for the tournament before auto-scheduling.\n\nGo to Edit Tournament and fill in the 'Start Time' field.");
      return;
    }

    if (!tournament.numberOfCourts || tournament.numberOfCourts < 1) {
      alert("Please set the Number of Courts for the tournament before auto-scheduling.\n\nGo to Edit Tournament and fill in the 'Number of Courts' field.");
      return;
    }

    setAutoScheduling(true);
    try {
      console.log("=== AUTO-SCHEDULE DEBUG ===");
      console.log("Tournament ID:", tournament.id);
      console.log("Starting auto-schedule with:", {
        startTime: tournament.startTime,
        numberOfCourts: tournament.numberOfCourts,
        startDate: tournament.startDate,
      });
      console.log("Groups:", tournament.groups?.length || 0);
      console.log("Knockout MastersCup:", tournament.knockoutBracket?.mastersCup?.length || 0);
      console.log("Knockout TourCup:", tournament.knockoutBracket?.tourCup?.length || 0);

      // Check if tournament is ready for scheduling
      const hasGroups = tournament.groups && tournament.groups.length > 0;
      const groupsWithTeams = tournament.groups?.filter(g => g.teamIds && g.teamIds.length >= 2) || [];
      const hasKnockout = (tournament.knockoutBracket?.mastersCup && tournament.knockoutBracket.mastersCup.length > 0) ||
                         (tournament.knockoutBracket?.tourCup && tournament.knockoutBracket.tourCup.length > 0);
      
      console.log("Groups with teams (≥2):", groupsWithTeams.length);

      // Formats without a group stage (e.g. single-elimination) go straight to a
      // knockout bracket, so only block scheduling when there's neither groups nor a bracket.
      if ((!hasGroups || groupsWithTeams.length === 0) && !hasKnockout) {
        alert(
          tournament.format === "single-elimination"
            ? "Cannot schedule matches - No bracket generated yet!\n\nGenerate the knockout bracket in the Bracket tab first, then try auto-scheduling again."
            : "Cannot schedule matches - No groups configured!\n\n" +
              "Please:\n" +
              "1. Create groups for the tournament\n" +
              "2. Assign at least 2 teams to each group\n" +
              "3. Then try auto-scheduling again"
        );
        setAutoScheduling(false);
        return;
      }

      // Refuse to schedule if any player is on more than one team
      const duplicates = detectDuplicatePlayers(tournament);
      if (duplicates.length > 0) {
        const lines = duplicates.map(d => `• ${d.playerName}: ${d.teamNames.join(', ')}`).join('\n');
        alert(`Cannot schedule — the following players appear in more than one team:\n\n${lines}\n\nRemove duplicate registrations before scheduling.`);
        setAutoScheduling(false);
        return;
      }

      // Run the auto-schedule algorithm (best of 8 attempts)
      const { result: scheduledTournament, bestScore, bestSeed } = autoScheduleMatchesBestOf(tournament, 8);
      console.log(`Auto-schedule complete — best fairness score: ${bestScore.toFixed(1)} (seed ${bestSeed})`);


      console.log("Updating tournament with scheduled matches...");

      // Update the tournament on the server
      const response = await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({
          groups: scheduledTournament.groups,
          knockoutBracket: scheduledTournament.knockoutBracket,
        }),
      });

      if (!response.ok) {
        throw new Error("Server returned an error");
      }

      console.log("Auto-schedule successful!");

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error auto-scheduling:", error);
      alert("Failed to auto-schedule matches. Please try again or check the console for details.");
    } finally {
      setAutoScheduling(false);
    }
  };

  const handleEditMatch = (item: ScheduleItem) => {
    if (!item.matchId) return;

    setEditingMatch(item.matchId);
    setEditTime(formatTime(item.time));
    setEditCourt(item.courtNumber || 1);
  };

  const handleSaveMatch = async (item: ScheduleItem) => {
    if (!tournament || !item.matchId) return;

    setSaving(true);
    try {
      // Prepare the update based on match type
      const updatedTournament = JSON.parse(JSON.stringify(tournament));

      // Construct ISO timestamp from date + time
      const baseDate = tournament.startDate
        ? new Date(tournament.startDate)
        : new Date();
      const [hours, minutes] = editTime.split(":").map(Number);
      baseDate.setHours(hours, minutes, 0, 0);
      const isoTime = baseDate.toISOString();

      if (item.type === "match" && item.groupIndex !== undefined && item.matchIndex !== undefined) {
        // Group match
        if (updatedTournament.groups && updatedTournament.groups[item.groupIndex]) {
          const group = updatedTournament.groups[item.groupIndex];
          if (group.matchResults && group.matchResults[item.matchIndex]) {
            group.matchResults[item.matchIndex].startTime = isoTime;
            group.matchResults[item.matchIndex].courtNumber = editCourt;
          }
        }
      } else if (item.bracketType && item.matchIndex !== undefined) {
        // Knockout match
        if (updatedTournament.knockoutBracket?.[item.bracketType]) {
          const bracket = updatedTournament.knockoutBracket[item.bracketType];
          if (bracket[item.matchIndex]) {
            bracket[item.matchIndex].startTime = isoTime;
            bracket[item.matchIndex].courtNumber = editCourt;
          }
        }
      }

      // Update on server
      await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({
          groups: updatedTournament.groups,
          knockoutBracket: updatedTournament.knockoutBracket,
        }),
      });

      setEditingMatch(null);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error updating match:", error);
      alert("Failed to update match");
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (time: string) => {
    if (time.includes("T")) {
      return new Date(time).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    return time;
  };

  // Helper to get team name by ID
  const getTeamName = (teamId: string): string => {
    const team = tournament.registeredTeams?.find((t) => t.id === teamId);
    if (!team) return "TBD";
    return team.teamName || `${team.player1Name} & ${team.player2Name}`;
  };

  // Build the schedule from tournament data
  const buildSchedule = (): ScheduleItem[] => {
    const scheduleItems: ScheduleItem[] = [];

    // Add events/breaks
    if (tournament.events && tournament.events.length > 0) {
      tournament.events.forEach((event) => {
        scheduleItems.push({
          time: event.startTime,
          type: "event",
          title: event.name,
          subtitle: event.type.charAt(0).toUpperCase() + event.type.slice(1),
          eventType: event.type,
          description: event.description,
          duration: event.duration,
        });
      });
    }

    // Add matches (if they have been scheduled)
    // Group stage matches
    if (tournament.groups) {
      tournament.groups.forEach((group, groupIndex) => {
        if (group.matchResults) {
          group.matchResults.forEach((match, matchIndex) => {
            if (match.startTime && match.courtNumber) {
              const t1 = tournament.registeredTeams?.find(t => t.id === match.team1Id);
              const t2 = tournament.registeredTeams?.find(t => t.id === match.team2Id);
              // Show player names only when at least one team uses a custom team name
              // (otherwise the title already shows the player names directly)
              const hasCustomName = !!(t1?.teamName || t2?.teamName);
              const players = hasCustomName
                ? `${t1?.player1Name || ''}/${t1?.player2Name || ''} · ${t2?.player1Name || ''}/${t2?.player2Name || ''}`
                : `${t1?.player1Name || ''}/${t1?.player2Name || ''} · ${t2?.player1Name || ''}/${t2?.player2Name || ''}`;
              scheduleItems.push({
                time: match.startTime,
                type: "match",
                title: `${getTeamName(match.team1Id)} vs ${getTeamName(match.team2Id)}`,
                subtitle: `Group ${String.fromCharCode(65 + groupIndex)}`,
                players,
                courtNumber: match.courtNumber,
                duration: tournament.timedMatchDuration,
                matchId: `group-${groupIndex}-${matchIndex}`,
                groupIndex,
                matchIndex,
              });
            }
          });
        }
      });
    }

    // Knockout matches
    if (tournament.knockoutBracket) {
      // Helper to get match label — matches the "Match #N" numbering shown on the card itself.
      const getMatchLabel = (matchNumber: number): string => {
        return `#${matchNumber + 1}`;
      };
      
      // Helper to get team display with match labels
      const getKnockoutTeamDisplay = (match: any, isTeam1: boolean): string => {
        const teamId = isTeam1 ? match.team1Id : match.team2Id;
        
        if (teamId) {
          return getTeamName(teamId);
        }
        
        // Check for previous match reference FIRST (for specific match labels)
        const previousMatchNum = isTeam1 ? match.previousMatch1 : match.previousMatch2;
        if (previousMatchNum !== undefined && previousMatchNum !== null) {
          // Determine if this match uses losers or winners from previous matches
          // - Consolation-semi-finals: Losers of quarter-finals
          // - Third-place: Losers of semi-finals
          // - Seventh-place: Losers of consolation-semi-finals
          // - Fifth-place: Winners of consolation-semi-finals
          // - All other matches: Winners
          const isLoserMatch = match.isConsolationMatch || 
            match.round === 'consolation-semi-finals' || 
            match.round === 'seventh-place' ||
            match.round === 'third-place';
          
          const winnerOrLoser = isLoserMatch ? 'Loser' : 'Winner';
          console.log(`[Schedule] Match ${match.matchNumber}: previousMatch${isTeam1 ? '1' : '2'} = ${previousMatchNum}, showing ${winnerOrLoser} of Match ${getMatchLabel(previousMatchNum)}`);
          return `${winnerOrLoser} of Match ${getMatchLabel(previousMatchNum)}`;
        }
        
        // Check for placeholder text (only if no previousMatch reference)
        const placeholder = isTeam1 ? match.team1Placeholder : match.team2Placeholder;
        if (placeholder && !placeholder.toLowerCase().includes('previous match')) {
          // Only use placeholder if it's specific (not generic "winner of previous match")
          console.log(`[Schedule] Match ${match.matchNumber}: using placeholder = ${placeholder}`);
          return placeholder;
        }
        
        console.log(`[Schedule] Match ${match.matchNumber} (${match.round}): returning TBD - no teamId, previousMatch${isTeam1 ? '1' : '2'} = ${previousMatchNum}, placeholder = ${placeholder}`);
        return "TBD";
      };
      
      const addKnockoutMatches = (
        matches: any[],
        bracketName: string,
        bracketType: "mastersCup" | "tourCup",
      ) => {
        matches.forEach((match, index) => {
          if (match.startTime) {
            // Handle placeholders and real matches
            const isPlaceholder = match.isPlaceholder || (!match.team1Id && !match.team2Id);
            
            let team1Name: string;
            let team2Name: string;
            
            // Use the same logic as TournamentDisplay
            team1Name = getKnockoutTeamDisplay(match, true);
            team2Name = getKnockoutTeamDisplay(match, false);

            scheduleItems.push({
              time: match.startTime,
              type: "match",
              title: `${team1Name} vs ${team2Name}`,
              subtitle: isPlaceholder 
                ? `${bracketName} - ${match.round || 'Reserved Slot'}`
                : `${bracketName} - ${match.round || 'Match ' + (index + 1)}`,
              courtNumber: match.courtNumber,
              duration: tournament.timedMatchDuration,
              matchId: `knockout-${bracketType}-${index}`,
              bracketType,
              matchIndex: index,
              isPlaceholder,
              isCancelled: match.isCancelled,
            });
          }
        });
      };

      // Masters Cup bracket
      if (tournament.knockoutBracket.mastersCup) {
        addKnockoutMatches(
          tournament.knockoutBracket.mastersCup,
          bracketNames.primary,
          "mastersCup",
        );
      }

      // Tour Cup bracket
      if (tournament.knockoutBracket.tourCup) {
        addKnockoutMatches(tournament.knockoutBracket.tourCup, bracketNames.secondary, "tourCup");
      }
    }

    // Court blocks (manually placed, occupy a single court like a match)
    if (tournament.scheduleBlocks) {
      tournament.scheduleBlocks.forEach((block) => {
        scheduleItems.push({
          time: block.startTime,
          type: "block",
          title: block.name,
          subtitle: "Blocked",
          courtNumber: block.courtNumber,
          duration: block.duration,
          matchId: `block-${block.id}`,
        });
      });
    }

    // Sort by time
    scheduleItems.sort((a, b) => {
      const timeA = a.time.includes("T") ? a.time : `2000-01-01T${a.time}`;
      const timeB = b.time.includes("T") ? b.time : `2000-01-01T${b.time}`;
      return new Date(timeA).getTime() - new Date(timeB).getTime();
    });

    return scheduleItems;
  };

  const schedule = buildSchedule();

  // Build grid structure: time slots x courts
  const buildScheduleGrid = () => {
    const numberOfCourts = tournament.numberOfCourts || 4;
    const matches = schedule.filter(item => (item.type === "match" || item.type === "block") && item.courtNumber);
    const events = schedule.filter(item => item.type === "event");
    
    // Get unique time slots from both matches AND events
    const timeSlots = new Set<string>();
    matches.forEach(match => {
      timeSlots.add(formatTime(match.time));
    });
    events.forEach(event => {
      timeSlots.add(formatTime(event.time));
    });
    
    const sortedTimeSlots = Array.from(timeSlots).sort();
    
    // Create grid: Map<time, Map<court, match>>
    const grid = new Map<string, Map<number, ScheduleItem>>();
    // Track which time slots have events
    const eventMap = new Map<string, ScheduleItem>();
    
    sortedTimeSlots.forEach(time => {
      grid.set(time, new Map());
    });
    
    matches.forEach(match => {
      const time = formatTime(match.time);
      const court = match.courtNumber!;
      grid.get(time)?.set(court, match);
    });
    
    events.forEach(event => {
      const time = formatTime(event.time);
      eventMap.set(time, event);
    });
    
    return { grid, sortedTimeSlots, numberOfCourts, events, eventMap };
  };
  
  const { grid, sortedTimeSlots, numberOfCourts, events, eventMap } = buildScheduleGrid();

  // Apply pending changes to grid for visual feedback
  const gridWithPendingChanges = new Map(grid);
  if (pendingChanges.size > 0) {
    // Clear all matches first
    gridWithPendingChanges.forEach((courtMap) => courtMap.clear());
    
    // Rebuild with pending changes applied
    const matchesWithChanges = schedule.filter(item => item.type === "match" || item.type === "block");
    matchesWithChanges.forEach(match => {
      if (!match.matchId) return;
      
      const pendingChange = pendingChanges.get(match.matchId);
      if (pendingChange) {
        // Use pending position
        const courtMap = gridWithPendingChanges.get(pendingChange.time);
        if (courtMap) {
          courtMap.set(pendingChange.court, { ...match, time: `2000-01-01T${pendingChange.time}` });
        }
      } else {
        // Use original position
        const time = formatTime(match.time);
        const courtMap = gridWithPendingChanges.get(time);
        if (courtMap && match.courtNumber) {
          courtMap.set(match.courtNumber, match);
        }
      }
    });
  }

  const calculateEndTime = (startTime: string, duration?: number) => {
    if (!duration) return null;

    const time = startTime.includes("T")
      ? new Date(startTime)
      : new Date(`2000-01-01T${startTime}`);

    time.setMinutes(time.getMinutes() + duration);

    return time.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper
        sx={{
          background: "linear-gradient(135deg, #2d3561 0%, #42a5f5 100%)",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ScheduleIcon />
          <Typography variant="h6" component="div">
            Tournament Schedule
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ pt: 1 }}>
        {/* Add Event inline form */}
        {showAddEventForm && !readOnly && (
          <Paper
            variant="outlined"
            sx={{ p: 2, mb: 2, borderColor: "primary.main", bgcolor: "primary.50" }}
          >
            <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
              Add Schedule Event
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Event Name"
                  value={newEventName}
                  onChange={(e) => setNewEventName(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="e.g. Lunch Break"
                  autoFocus
                />
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={newEventType}
                    onChange={(e) => setNewEventType(e.target.value as TournamentEventType)}
                    label="Type"
                  >
                    <MenuItem value="break">Break</MenuItem>
                    <MenuItem value="ceremony">Ceremony</MenuItem>
                    <MenuItem value="tombola">Tombola</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={newEventTime}
                  onChange={(e) => setNewEventTime(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Duration (min)"
                  type="number"
                  value={newEventDuration}
                  onChange={(e) => setNewEventDuration(Math.max(1, Number(e.target.value)))}
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1, max: 480 }}
                />
                <TextField
                  label="Description (optional)"
                  value={newEventDescription}
                  onChange={(e) => setNewEventDescription(e.target.value)}
                  size="small"
                  sx={{ flex: 2 }}
                />
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setShowAddEventForm(false);
                    setNewEventName("");
                    setNewEventTime("");
                    setNewEventDuration(30);
                    setNewEventType("break");
                    setNewEventDescription("");
                  }}
                  disabled={addingEvent}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddEvent}
                  disabled={addingEvent || !newEventName.trim() || !newEventTime}
                  startIcon={<SaveIcon />}
                >
                  {addingEvent ? "Saving & Rescheduling…" : "Save Event & Reschedule"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Add Block inline form */}
        {showAddBlockForm && !readOnly && (
          <Paper
            variant="outlined"
            sx={{ p: 2, mb: 2, borderColor: "text.secondary", bgcolor: "action.hover" }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Add Court Block
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                label="Block Name"
                value={newBlockName}
                onChange={(e) => setNewBlockName(e.target.value)}
                size="small"
                fullWidth
                placeholder="e.g. Outside Booking, Court Maintenance"
                autoFocus
              />
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={newBlockTime}
                  onChange={(e) => setNewBlockTime(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Duration (min)"
                  type="number"
                  value={newBlockDuration}
                  onChange={(e) => setNewBlockDuration(Math.max(1, Number(e.target.value)))}
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1, max: 480 }}
                />
              </Stack>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Court(s)
                </Typography>
                <FormGroup row>
                  {Array.from({ length: tournament.numberOfCourts || 4 }, (_, i) => i + 1).map((court) => (
                    <FormControlLabel
                      key={court}
                      control={
                        <Checkbox
                          size="small"
                          checked={newBlockCourts.includes(court)}
                          onChange={(e) => {
                            setNewBlockCourts((prev) =>
                              e.target.checked ? [...prev, court] : prev.filter((c) => c !== court)
                            );
                          }}
                        />
                      }
                      label={`Court ${court}`}
                    />
                  ))}
                </FormGroup>
              </Box>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setShowAddBlockForm(false);
                    setNewBlockName("");
                    setNewBlockTime("");
                    setNewBlockDuration(30);
                    setNewBlockCourts([]);
                  }}
                  disabled={addingBlock}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddBlock}
                  disabled={addingBlock || !newBlockName.trim() || !newBlockTime || newBlockCourts.length === 0}
                  startIcon={<SaveIcon />}
                >
                  {addingBlock ? "Saving…" : "Save Block"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Profit preview — always visible when a schedule exists */}
        {profitPreview && (
          <Alert
            severity={
              !profitPreview.pricePerSlot && !profitPreview.feePerTeam
                ? "info"
                : profitPreview.profit >= 0
                ? "success"
                : "error"
            }
            sx={{ mb: 3 }}
          >
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              💰 Schedule Profit Preview
            </Typography>

            {/* Court booking breakdown */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Court bookings ({profitPreview.startLabel} → {profitPreview.endLabel})
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
                {profitPreview.bookingWindows.map((w, i) => {
                  const label = new Date(w.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                  const partial = w.billedMinutes < 90;
                  return (
                    <Chip
                      key={i}
                      label={`${label}: ${w.courts.size} court${w.courts.size !== 1 ? "s" : ""}${partial ? ` (${w.billedMinutes}min)` : ""}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  );
                })}
              </Box>
              {profitPreview.pricePerSlot ? (
                <Typography variant="body2">
                  Court cost:{" "}
                  {profitPreview.bookingWindows.map((w, i) => {
                    const fraction = w.billedMinutes / 90;
                    const slotCost = fraction * (profitPreview.pricePerSlot ?? 0);
                    return (
                      <span key={i}>
                        {i > 0 && " + "}
                        {w.courts.size}×€{slotCost.toFixed(2)}{w.billedMinutes < 90 ? ` (${w.billedMinutes}min)` : ""}
                      </span>
                    );
                  })}{" "}
                  = <strong style={{ color: "#d32f2f" }}>−€{profitPreview.totalCourtCost.toFixed(2)}</strong>
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Court price not set — add it in tournament settings.
                </Typography>
              )}
            </Box>

            {/* Revenue */}
            {profitPreview.feePerTeam ? (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Revenue: {tournament.registeredTeams?.length || 0} teams × €{profitPreview.feePerTeam}{" "}
                = <strong style={{ color: "#2e7d32" }}>+€{profitPreview.totalRevenue.toFixed(2)}</strong>
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 1 }}>
                Registration fee not set — add it in tournament settings.
              </Typography>
            )}

            {/* Profit / Loss total */}
            {profitPreview.pricePerSlot && profitPreview.feePerTeam && (
              <Typography variant="body1" fontWeight="bold">
                {profitPreview.profit >= 0 ? "Profit" : "Loss"}:{" "}
                <span style={{ color: profitPreview.profit >= 0 ? "#2e7d32" : "#d32f2f" }}>
                  {profitPreview.profit >= 0 ? "+" : ""}€{profitPreview.profit.toFixed(2)}
                </span>
              </Typography>
            )}
          </Alert>
        )}

        {/* Tournament Info */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 3,
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
            {tournament.name}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {tournament.venue && (
              <Chip
                icon={<VenueIcon />}
                label={tournament.venue}
                size="small"
                sx={{ bgcolor: "white" }}
              />
            )}
            {tournament.startDate && (
              <Chip
                icon={<EventIcon />}
                label={new Date(tournament.startDate).toLocaleDateString()}
                size="small"
                sx={{ bgcolor: "white" }}
              />
            )}
            {tournament.startTime && (
              <Chip
                label={`Starts at ${tournament.startTime}`}
                size="small"
                sx={{ bgcolor: "white" }}
              />
            )}
            {tournament.numberOfCourts && (
              <Chip
                label={`${tournament.numberOfCourts} Courts`}
                size="small"
                sx={{ bgcolor: "white" }}
              />
            )}
          </Stack>
        </Paper>

        {/* Waiting Time Analysis Summary */}
        {waitingAnalysis.playerSchedules.length > 0 && (
          <Alert 
            severity={waitingAnalysis.fairnessScore > 70 ? "success" : waitingAnalysis.fairnessScore > 40 ? "warning" : "error"} 
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" fontWeight="bold">Player Waiting Time Analysis</Typography>
            <Typography variant="body2">
              Average wait: <strong>{Math.round(waitingAnalysis.tournamentAverage)} minutes</strong> • 
              Fairness score: <strong>{Math.round(waitingAnalysis.fairnessScore)}/100</strong>
              {waitingAnalysis.fairnessScore > 70 && " ✓ Well balanced"}
              {waitingAnalysis.fairnessScore <= 70 && waitingAnalysis.fairnessScore > 40 && " ⚠️ Moderate imbalance"}
              {waitingAnalysis.fairnessScore <= 40 && " ❌ Highly imbalanced"}
            </Typography>
            {waitingAnalysis.warnings.length > 0 && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {waitingAnalysis.warnings.slice(0, 3).join(" • ")}
                {waitingAnalysis.warnings.length > 3 && ` • +${waitingAnalysis.warnings.length - 3} more`}
              </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: "italic" }}>
              Color coding: 🟢 Good (&gt;35min) • 🟡 Tight (20-35min) • 🔴 Rushed (&lt;20min)
            </Typography>
          </Alert>
        )}

        {/* Info about knockout scheduling */}
        {tournament.format === "groups-knockout" && !tournament.knockoutBracket?.mastersCup?.some(m => !m.isPlaceholder) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Knockout Scheduling:</strong> Reserved time slots are shown below (marked with dashed borders) for knockout matches. 
            When you generate the knockout bracket after group stage completion, 
            the actual matches will automatically fill these reserved slots with team names. 
            Format: {tournament.knockoutFormatType === "double-bracket" ? `${bracketNames.primary} & ${bracketNames.secondary} (dual brackets)` : "Single Elimination"}
          </Alert>
        )}

        {/* Schedule Grid */}
        {sortedTimeSlots.length > 0 ? (
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <ScheduleIcon color="primary" />
                  Court Schedule
                  {pendingChanges.size > 0 && (
                    <Chip
                      label={`${pendingChanges.size} unsaved changes`}
                      color="warning"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>

                {/* Drag mode toggle */}
                {!readOnly && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Tooltip title="Cascade mode: dragging a card shifts all cards in between">
                      <Box
                        onClick={() => setSwapMode(false)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1.5,
                          py: 0.75,
                          cursor: "pointer",
                          bgcolor: !swapMode ? "primary.main" : "transparent",
                          color: !swapMode ? "white" : "text.secondary",
                          transition: "all 0.2s",
                          "&:hover": { bgcolor: !swapMode ? "primary.dark" : "action.hover" },
                        }}
                      >
                        <CascadeIcon sx={{ fontSize: 18 }} />
                        <Typography variant="caption" fontWeight={!swapMode ? 700 : 400}>
                          Cascade
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Tooltip title="Swap mode: dragging a card onto another swaps only those two">
                      <Box
                        onClick={() => setSwapMode(true)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1.5,
                          py: 0.75,
                          cursor: "pointer",
                          bgcolor: swapMode ? "secondary.main" : "transparent",
                          color: swapMode ? "white" : "text.secondary",
                          transition: "all 0.2s",
                          "&:hover": { bgcolor: swapMode ? "secondary.dark" : "action.hover" },
                        }}
                      >
                        <SwapIcon sx={{ fontSize: 18 }} />
                        <Typography variant="caption" fontWeight={swapMode ? 700 : 400}>
                          Swap
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                )}
              </Box>

            {/* Events are now integrated into the schedule grid below */}

            {/* Court Grid Table */}
            <Paper elevation={2} sx={{ overflow: "auto" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `80px repeat(${numberOfCourts}, 1fr)`,
                  minWidth: 600,
                }}
              >
                {/* Header Row */}
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "primary.main",
                    color: "white",
                    fontWeight: "bold",
                    textAlign: "center",
                    borderRight: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  Time
                </Box>
                {Array.from({ length: numberOfCourts }, (_, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 1.5,
                      bgcolor: "primary.main",
                      color: "white",
                      fontWeight: "bold",
                      textAlign: "center",
                      borderRight: i < numberOfCourts - 1 ? "1px solid rgba(255,255,255,0.2)" : "none",
                    }}
                  >
                    Court {i + 1}
                  </Box>
                ))}

                {/* Grid Rows */}
                {sortedTimeSlots.map((time, rowIndex) => {
                  const courtMatches = gridWithPendingChanges.get(time);
                  const event = eventMap.get(time);
                  
                  // If this time slot has an event, render it spanning all columns
                  if (event) {
                    return (
                      <React.Fragment key={time}>
                        {/* Time Cell */}
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "background.default",
                            borderRight: "1px solid",
                            borderBottom: rowIndex < sortedTimeSlots.length - 1 ? "1px solid" : "none",
                            borderColor: "divider",
                            fontWeight: "600",
                            color: getEventColor(event.eventType || ""),
                            textAlign: "center",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {time}
                        </Box>
                        
                        {/* Event spanning all courts */}
                        <Box
                          sx={{
                            gridColumn: `span ${numberOfCourts}`,
                            p: 2,
                            bgcolor: "background.default",
                            borderBottom: rowIndex < sortedTimeSlots.length - 1 ? "1px solid" : "none",
                            borderColor: "divider",
                          }}
                        >
                          <Paper
                            elevation={2}
                            sx={{
                              p: 1.5,
                              borderLeft: "4px solid",
                              borderLeftColor: getEventColor(event.eventType || ""),
                              bgcolor: `${getEventColor(event.eventType || "")}15`,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: getEventColor(event.eventType || ""),
                                }}
                              >
                                {getEventIcon(event.eventType || "")}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" fontWeight="600">
                                  {event.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {event.subtitle} • {event.duration} minutes
                                  {event.description && ` • ${event.description}`}
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>
                        </Box>
                      </React.Fragment>
                    );
                  }
                  
                  // Regular match row
                  return (
                    <React.Fragment key={time}>
                      {/* Time Cell */}
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: rowIndex % 2 === 0 ? "background.default" : "background.paper",
                          borderRight: "1px solid",
                          borderBottom: rowIndex < sortedTimeSlots.length - 1 ? "1px solid" : "none",
                          borderColor: "divider",
                          fontWeight: "600",
                          color: "primary.main",
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {time}
                      </Box>

                      {/* Court Cells */}
                      {Array.from({ length: numberOfCourts }, (_, courtIndex) => {
                        const courtNumber = courtIndex + 1;
                        const match = courtMatches?.get(courtNumber);
                        const isEditing = editingMatch === match?.matchId;
                        const isPendingChange = match?.matchId && pendingChanges.has(match.matchId);

                        return (
                          <DroppableCell
                            key={courtNumber}
                            id={`slot-${time}-${courtNumber}`}
                            disabled={readOnly || isEditing}
                          >
                            {(isOver) => (
                              <Box
                                sx={{
                                  p: 1,
                                  bgcolor: isOver
                                    ? "primary.lighter" 
                                    : (isPendingChange ? "warning.lighter" : (rowIndex % 2 === 0 ? "background.default" : "background.paper")),
                                  borderRight: courtIndex < numberOfCourts - 1 ? "1px solid" : "none",
                                  borderBottom: rowIndex < sortedTimeSlots.length - 1 ? "1px solid" : "none",
                                  borderColor: "divider",
                                  minHeight: 80,
                                  height: "100%",
                                  position: "relative",
                                  transition: "background-color 0.2s",
                                }}
                              >
                                {match ? (
                                  <MatchCardContent
                                    match={match}
                                    isPendingChange={isPendingChange}
                                    isEditing={isEditing}
                                    readOnly={readOnly}
                                    numberOfCourts={numberOfCourts}
                                    editTime={editTime}
                                    setEditTime={setEditTime}
                                    editCourt={editCourt}
                                    setEditCourt={setEditCourt}
                                    saving={saving}
                                    handleSaveMatch={handleSaveMatch}
                                    setEditingMatch={setEditingMatch}
                                    getWaitingTimeColor={getWaitingTimeColor}
                                    onCancelKnockout={handleCancelKnockoutMatch}
                                    onUncancelKnockout={handleUncancelKnockoutMatch}
                                    isCancelling={cancellingMatch === match.matchId}
                                    onDeleteBlock={handleDeleteBlock}
                                  />
                                ) : (
                                  // Empty Cell
                                  <Box
                                    sx={{
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "text.disabled",
                                    }}
                                  >
                                    <Typography variant="caption">—</Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </DroppableCell>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Paper>
            </Box>
          {/* Drag overlay - follows cursor while dragging */}
          <DragOverlay dropAnimation={null}>
            {activeDragId ? (() => {
              const activeMatch = schedule.find(m => m.matchId === activeDragId);
              if (!activeMatch) return null;
              const waitingColor = !activeMatch.isPlaceholder ? getWaitingTimeColor(activeMatch.matchId || '') : null;
              const isPending = activeMatch.matchId && pendingChanges.has(activeMatch.matchId);
              return (
                <Paper
                  elevation={8}
                  sx={{
                    p: 1,
                    width: 200,
                    bgcolor: isPending
                      ? "warning.lighter"
                      : (activeMatch.isPlaceholder ? "action.hover" : (waitingColor?.bg || "info.lighter")),
                    borderLeft: "3px solid",
                    borderLeftColor: isPending
                      ? "warning.main"
                      : (activeMatch.isPlaceholder ? "warning.main" : (waitingColor?.border || "info.main")),
                    opacity: 0.9,
                    cursor: "grabbing",
                    userSelect: "none",
                    transform: "rotate(2deg)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <DragIcon fontSize="small" sx={{ color: "text.secondary", fontSize: "1rem" }} />
                    <Typography variant="body2" fontWeight="600" sx={{ fontSize: "0.85rem" }}>
                      {activeMatch.title}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {activeMatch.subtitle}
                  </Typography>
                </Paper>
              );
            })() : null}
          </DragOverlay>
          </DndContext>
        ) : (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <ScheduleIcon sx={{ fontSize: 80, color: "#ccc", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No schedule available yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {tournament.startTime
                ? "Matches haven't been scheduled yet. Generate groups or knockout bracket to see the schedule."
                : "Set a start time and add events to create a schedule."}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ p: 1, pt: 2, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {!readOnly && tournament.startTime && tournament.numberOfCourts && (
            <Tooltip title="Schedules all matches across available courts. Will reschedule existing matches with new settings.">
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AutoScheduleIcon />}
                onClick={handleAutoSchedule}
                disabled={autoScheduling || saving || pendingChanges.size > 0 || addingEvent}
              >
                {autoScheduling ? "Scheduling..." : "Auto Schedule All"}
              </Button>
            </Tooltip>
          )}
          {!readOnly && (
            <Tooltip title="Add a break, ceremony, or other event. Conflicting matches will be pushed to after the event.">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddEventIcon />}
                onClick={() => setShowAddEventForm(v => !v)}
                disabled={autoScheduling || saving || pendingChanges.size > 0 || addingEvent}
              >
                {showAddEventForm ? "Cancel" : "Add Event"}
              </Button>
            </Tooltip>
          )}
          {!readOnly && (
            <Tooltip title="Reserve a court for a time window (outside booking, maintenance, etc). Drag it into place afterward, just like a match.">
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<BlockIcon />}
                onClick={() => setShowAddBlockForm(v => !v)}
                disabled={autoScheduling || saving || pendingChanges.size > 0 || addingBlock}
              >
                {showAddBlockForm ? "Cancel" : "Add Block"}
              </Button>
            </Tooltip>
          )}
          {waitingAnalysis.playerSchedules.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<ScheduleIcon />}
              onClick={() => setShowWaitingReport(true)}
              disabled={pendingChanges.size > 0}
            >
              Waiting Time Report
            </Button>
          )}
        </Box>
        
        <Box sx={{ display: "flex", gap: 1 }}>
          {pendingChanges.size > 0 && (
            <>
              <Button 
                onClick={handleDiscardChanges} 
                variant="outlined"
                color="error"
                startIcon={<UndoIcon />}
                disabled={saving}
              >
                Discard {pendingChanges.size} Change{pendingChanges.size > 1 ? 's' : ''}
              </Button>
              <Button 
                onClick={handleSavePendingChanges} 
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                disabled={saving}
              >
                {saving ? "Saving..." : `Save ${pendingChanges.size} Change${pendingChanges.size > 1 ? 's' : ''}`}
              </Button>
            </>
          )}
        </Box>
      </Box>
      
      {/* Error/Info Snackbar */}
      <Snackbar
        open={showDragError}
        autoHideDuration={6000}
        onClose={() => setShowDragError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowDragError(false)} 
          severity={dragError?.includes('conflict') || dragError?.includes('Cannot') ? 'error' : 'info'}
          sx={{ width: '100%' }}
        >
          {dragError}
        </Alert>
      </Snackbar>
      
      {/* Waiting Time Report Dialog */}
      <WaitingTimeReportDialog
        open={showWaitingReport}
        onClose={() => setShowWaitingReport(false)}
        tournament={tournament}
      />
    </Box>
  );
};

export default TournamentSchedulePanel;
